import { useCallback } from "react";

import { useDispatch } from "react-redux";

import {
  combineRunProps,
  type OverrideRunProps,
  useRequestWithFeedback,
  parseErrorResponse,
  useIdempotencyErrorHandling,
  type AnyException,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useFeature } from "../features";
import { actions } from "../../entities/transfers";
import { useLibrary } from "../../../providers";
import { useInstitution } from "../../entities";

import {
  TransferForm,
  transformTransferFundsFormFieldsToApiFields,
} from "./TransferFunds.form";
import {
  useIsScheduledTransfer,
  useSelectedDestinationAccount,
  useSubmittedTransferId,
} from "./hooks";
import { API } from "./api";

import type { ApiError } from "../../../../types/models/v1";

export const useCheckDestinationInstantEligibility = () => {
  const { send, loading } = useRequestWithFeedback<
    API.InstantEligibilityResponse,
    AnyException
  >();
  const dispatch = useDispatch();
  const { featureEnabled } = useFeature();
  const institution = useInstitution();
  const destination = useSelectedDestinationAccount();
  const checkDestinationInstantEligibility = useCallback(
    (
      overrideRunProps: OverrideRunProps<
        API.InstantEligibilityResponse,
        AnyException
      > = {},
    ) => {
      if (
        !destination ||
        destination.source === "institution" ||
        destination.routing === institution?.routing_number ||
        !featureEnabled("fednow_send")
      ) {
        dispatch(actions.resetEligible(false));
        return;
      }
      send({
        action: API.checkInstantEligible({
          routing_number: destination.routing,
        }),
        ...combineRunProps<API.InstantEligibilityResponse, AnyException>(
          {
            onError: (_) => {
              dispatch(actions.resetEligible(false));
            },
            onSuccess(data) {
              dispatch(actions.eligible(data));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [destination, send, dispatch, featureEnabled, institution?.routing_number],
  );

  return useObjectMemo({ checkDestinationInstantEligibility, loading });
};

const canReceiveInstant = (data: API.InstantEligibilityResponse): boolean =>
  data.online &&
  ["receive", "send_receive", "rfp", "rfp_send"].includes(
    data.enrollment_level,
  );

// A batch can check multiple banks at once; the feedback sender drops calls while busy.
export const useCheckBulkReceiveInstantEligibility = () => {
  const { fetchApi } = useLibrary("network");
  return useCallback(
    async (routingNumber: string): Promise<boolean> => {
      const { url, options } = API.checkInstantEligible({
        routing_number: routingNumber,
      });
      const response = await fetchApi(url, options);
      const data = (await response.json()) as API.InstantEligibilityResponse;
      return canReceiveInstant(data);
    },
    [fetchApi],
  );
};

export const useCheckReceiveInstantEligibility = () => {
  const { send, loading } = useRequestWithFeedback<
    API.InstantEligibilityResponse,
    AnyException
  >();
  const { featureEnabled } = useFeature();

  const checkReceiveInstantEligibility = useCallback(
    (
      routingNumber: string,
      overrideRunProps: OverrideRunProps<
        API.InstantEligibilityResponse,
        AnyException
      > = {},
    ) => {
      if (!routingNumber || !featureEnabled("fednow_send")) {
        return Promise.resolve(false);
      }

      return new Promise<boolean>((resolve, reject) => {
        send({
          action: API.checkInstantEligible({ routing_number: routingNumber }),
          ...combineRunProps<API.InstantEligibilityResponse, AnyException>(
            {
              onError: (err) => {
                console.error("Error fetching instant eligibility", err);
                reject(err);
              },
              onSuccess: (data) => {
                resolve(canReceiveInstant(data));
              },
            },
            overrideRunProps,
          ),
        });
      });
    },
    [send, featureEnabled],
  );

  return useObjectMemo({ checkReceiveInstantEligibility, loading });
};

export const useGetInstantTransferFees = () => {
  const { send, loading } = useRequestWithFeedback<
    API.InstantFeesResponse,
    AnyException
  >();
  const dispatch = useDispatch();
  const { featureEnabled } = useFeature();
  const destination = useSelectedDestinationAccount();
  const getInstantTransferFees = useCallback(
    (
      overrideRunProps: OverrideRunProps<
        API.InstantFeesResponse,
        AnyException
      > = {},
    ) => {
      if (
        !destination ||
        destination.source === "institution" ||
        !featureEnabled("fednow_send")
      ) {
        return;
      }
      send({
        action: API.getInstantTransferFees(),
        ...combineRunProps<API.InstantFeesResponse, AnyException>(
          {
            onError: (_) => {},
            onSuccess(data) {
              dispatch(actions.setFees(data));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [destination, send, dispatch, featureEnabled],
  );
  return useObjectMemo({ getInstantTransferFees, loading });
};

export const useSubmitTransfer = (idempotencyKey: UUID) => {
  const { send, loading } = useRequestWithFeedback<
    API.TransferResponse,
    AnyException
  >();
  const dispatch = useDispatch();
  const form = TransferForm.useForm();
  const isScheduledTransfer = useIsScheduledTransfer();
  const { throwToast } = useLibrary("toasts");
  const t = useLibrary("translations");
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "transfer-idempotency-error",
  );

  const submitTransfer = useCallback(
    ({
      deviceIds,
      ...overrideRunProps
    }: OverrideRunProps<API.TransferResponse, AnyException> & {
      // freshly captured fingerprint IDs; form.values is from the last render,
      // so IDs set on the form just before submitting would not be seen yet
      deviceIds?: { deviceId: string; deviceRequestId: string };
    } = {}) => {
      return new Promise<{
        data?: API.TransferResponse;
        error?: AnyException;
      }>((resolve) => {
        const args = {
          payload: transformTransferFundsFormFieldsToApiFields({
            ...form.values,
            ...deviceIds,
          }),
          idempotencyKey,
        };
        send({
          action: isScheduledTransfer
            ? API.submitTransferScheduled(args)
            : API.submitTransferImmediate(args),
          ...combineRunProps<API.TransferResponse, AnyException>(
            {
              onError: async (err) => {
                const { errors } = await parseErrorResponse(err);
                const defaultError = t.getString(
                  "transfer-generic-error",
                  {},
                  "We could not process your transfer. Please try again or contact support.",
                );
                const message = errors.length
                  ? `${errors[0].description}`
                  : defaultError;
                if (message === "Instant transfer was rejected.") {
                  dispatch(actions.setTransferRejected());
                  return;
                }
                // Show the receipt instead of an error so the customer doesn't resend a payment that may have gone out.
                if (errors[0]?.code === "fednow_outcome_unknown") {
                  dispatch(actions.setTransferOutcomeUnknown());
                  return;
                }
                if (canHandleIdempotencyErrors(errors)) {
                  resolve({ error: { message } as ApiError });
                  return;
                }
                throwToast({
                  kind: "error",
                  message,
                });
                resolve({ error: { message } as ApiError });
              },
              onSuccess: (data) => {
                if (
                  (data.transfer as API.InstantTransfer)?.payment_identification
                ) {
                  dispatch(actions.setInstantTransferCreated(data));
                  resolve({ data });
                } else {
                  dispatch(actions.setTransferCreated(data));
                  resolve({ data });
                }
              },
            },
            overrideRunProps,
          ),
        });
      });
    },
    [
      send,
      dispatch,
      form.values,
      idempotencyKey,
      isScheduledTransfer,
      throwToast,
      t,
      canHandleIdempotencyErrors,
    ],
  );

  return useObjectMemo({ submitTransfer, loading });
};

export const useGetDownstreamInstantPayment = () => {
  const { send, loading } = useRequestWithFeedback<
    API.FedNowPayment,
    AnyException
  >();
  const dispatch = useDispatch();
  const paymentId = useSubmittedTransferId();
  const getInstantPayment = useCallback(
    (
      overrideRunProps: OverrideRunProps<API.FedNowPayment, AnyException> = {},
    ) => {
      if (!paymentId) {
        return;
      }
      send({
        action: API.getFedNowPayment({
          paymentIdentification: paymentId,
        }),
        ...combineRunProps<API.FedNowPayment, AnyException>(
          {
            onError: (_) => {},
            onSuccess(data) {
              dispatch(actions.setFedNowPayment(data));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [paymentId, send, dispatch],
  );

  return useObjectMemo({ getInstantPayment, loading });
};

export const useGetInstantOrSameDayFees = () => {
  const { send, loading } = useRequestWithFeedback<
    API.InstantFeesResponse,
    AnyException
  >();
  const { featureEnabled } = useFeature();

  const getInstantOrSameDayFees = useCallback(
    (
      paymentType: "instant" | "sameDay",
      overrideRunProps: OverrideRunProps<
        API.InstantFeesResponse,
        AnyException
      > = {},
    ) => {
      return new Promise<API.InstantFeesResponse | null>((resolve, reject) => {
        const getInstantFees = paymentType === "instant";
        const getSameDayFees = paymentType === "sameDay";
        if (getInstantFees && !featureEnabled("fednow_send")) {
          resolve(null);
          return;
        }
        if (getSameDayFees && !featureEnabled("same_day_ach")) {
          resolve(null);
          return;
        }
        send({
          action: getInstantFees
            ? API.getInstantTransferFees()
            : API.getSameDayACHFees(),
          ...combineRunProps<API.InstantFeesResponse, AnyException>(
            {
              onError: (err) => {
                console.error("Error fetching fees", err);
                reject(err);
              },
              onSuccess: (data) => {
                resolve(data);
              },
            },
            overrideRunProps,
          ),
        });
      });
    },
    [send, featureEnabled],
  );

  return useObjectMemo({ getInstantOrSameDayFees, loading });
};

export const useResetTransfer = () => {
  const dispatch = useDispatch();
  return useCallback(() => {
    dispatch(actions.resetTransferSubmitted());
  }, [dispatch]);
};
